import { Day } from '@codidae/common-types';
import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import Altus from 'components/altus/altus';
import { AppGroupScreentime } from 'components/app-group-screentime/app-group-screentime';
import Button from 'components/button/button';
import Flex from 'components/flex/flex';
import { useEffect, useState } from 'react';
import { BiChevronRight } from 'react-icons/bi';
import { GiSmartphone } from 'react-icons/gi';
import { useSearchParams } from 'react-router-dom';
import { getChildren } from 'services/childrens';
import { getScreentimeLimits, lockEveryApplication, unlockEveryApplication } from 'services/screentime';
import styles from './parent-settings-parental-control.module.scss';
import ParentalControlAltus from './tabs/parental-control-altus';

/* eslint-disable-next-line */
export interface ParentSettingsParentalControlProps {
  childrenId: string;
}

export function ParentSettingsParentalControl({ childrenId }: ParentSettingsParentalControlProps) {
  const [tab, setTab] = useState<'altus' | 'calendar' | 'apps' | null>(null);
  const [searchParams] = useSearchParams();

  const [{ data: children }] = useQueries({
    queries: [
      {
        queryKey: ['children', childrenId],
        queryFn: () => getChildren(childrenId),
      },
    ],
  });
  const { data: screentimeLimits, refetch: refetchScreentimeLimits } = useQuery({
    queryKey: ['getScreentimeLimits', childrenId],
    queryFn: () => getScreentimeLimits(Number(childrenId)),
  });
  const isFullyLocked = screentimeLimits?.some((limit) => limit.isInstantBlock);
  const lockScreentimeMutation = useMutation({
    mutationFn: lockEveryApplication,
    onSuccess: () => refetchScreentimeLimits(),
  });
  const unlockScreentimeMutation = useMutation({
    mutationFn: unlockEveryApplication,
    onSuccess: () => refetchScreentimeLimits(),
  });

  const initialTime = children?.weekControlParameters?.[new Date().getDay() as Day]?.initialDailyTime;
  const difficulty = children?.weekControlParameters?.[new Date().getDay() as Day]?.earnedTimeBase;

  useEffect(() => {
    const initialTab = searchParams.get('tab');
    if (initialTab === 'altus' || initialTab === 'apps') {
      setTab(initialTab as 'altus' | 'apps');
    }
  }, [searchParams]);

  if (tab === 'altus') {
    return <ParentalControlAltus childrenId={childrenId} onBack={() => setTab(null)} />;
  }
  if (tab === 'apps') {
    return <AppGroupScreentime childrenId={childrenId} onBack={() => setTab(null)} />;
  }
  return (
    <Flex
      width="100%"
      direction="column"
      gap={15}
      height="100%"
      pad={20}
      style={{
        overflow: 'auto',
      }}
    >
      <Flex width="100%" direction="column" gap={10} className={styles.weekSettings}>
        <div className={styles.weekSettingsTitle}>2 paramètres pour adapter l&apos;usage numérique de votre enfant</div>
        <button className={styles.weekSettingsButton} type="button" onClick={() => setTab('altus')}>
          <Altus type="hi" width={30} />
          <div className={styles.weekSettingsButtonText}>
            <span className={styles.main}>Mode Altus</span>
            <span>
              Temps initial: {initialTime} m - {difficulty} m / quiz
            </span>
          </div>
          <BiChevronRight size={23} />
        </button>
        {/* <button className={styles.weekSettingsButton} type="button" onClick={() => setTab('calendar')}>
          <BiTime size={30} />
          <div className={styles.weekSettingsButtonText}>
            <span className={styles.main}>Horaires</span>
            <span>X créneaux bloqués</span>
          </div>
          <BiChevronRight size={23} />
        </button> */}
        <button className={styles.weekSettingsButton} type="button" onClick={() => setTab('apps')}>
          <GiSmartphone size={30} />
          <div className={styles.weekSettingsButtonText}>
            <span className={styles.main}>Applications</span>
            <span>{screentimeLimits?.length} apps limitées</span>
          </div>
          <BiChevronRight size={23} />
        </button>
      </Flex>
      <Flex width="100%" direction="column" gap={10} className={styles.weekSettings}>
        <div className={styles.weekSettingsTitle}>Bloquer toutes les applications</div>
        <Button
          onClick={() => {
            if (isFullyLocked && children) {
              unlockScreentimeMutation.mutate(children?.id);
            } else if (children) {
              lockScreentimeMutation.mutate(children?.id);
            }
          }}
          outline
          loading={lockScreentimeMutation.isPending || unlockScreentimeMutation.isPending}
          color={isFullyLocked ? 'green' : 'red'}
        >
          {isFullyLocked ? 'DÉBLOQUER' : 'BLOQUER'} TOUTES LES APPS MAINTENANT
        </Button>
      </Flex>
    </Flex>
  );
}

export default ParentSettingsParentalControl;
